import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, isValid } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { Calendar, Clock, Star, List, LayoutGrid, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAthleteHistoryStore } from "@/store/api/athleteStore";
import { SessionRating } from "../attendance/SessionRating";
import { cn } from "@/lib/utils";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";

type ViewMode = "cards" | "table";

interface AthleteHistoryProps {
  compact?: boolean;
}

export function AthleteHistory({ compact = false }: AthleteHistoryProps) {
  const { t, i18n } = useTranslation(["athlete", "classes", "appui"]);
  const dateLocale = i18n.language === "fr" ? fr : enUS;
  const { athleteTheme } = useThemeCustomization();
  const isVb20 = athleteTheme.variant === "dark-premium";

  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("cards"); // "table" | "cards"
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [ratingModal, setRatingModal] = useState<{
    open: boolean;
    itemId?: string;
    attendanceId?: string;
  }>({ open: false });

  const { history, fetchHistory, isLoading, error, updateHistoryRating } =
    useAthleteHistoryStore();

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // L'historique n'affiche jamais une séance future, quel que soit son statut.
  const completedHistory = useMemo(
    () => history.filter((item) => {
      const startsAt = new Date(item.date).getTime();
      return Number.isFinite(startsAt) && startsAt < Date.now();
    }),
    [history]
  );

  // 2) Search (titre/coach)
  const filteredHistory = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return completedHistory;
    return completedHistory.filter((item) => {
      const inTitle = item.title?.toLowerCase().includes(term);
      const inCoach = item.coach?.toLowerCase().includes(term);
      return inTitle || inCoach;
    });
  }, [completedHistory, searchTerm]);

  // 3) Pagination (front)
  const total = filteredHistory.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const startIndex = (safePage - 1) * pageSize;
  const currentItems = filteredHistory.slice(startIndex, startIndex + pageSize);

  // Stats
  const toRate = completedHistory.filter((i) => !i.rating);
  const ratedCount = completedHistory.filter((i) => i.rating).length;
  const completedCount = completedHistory.length;
  const percentRated =
    completedCount > 0 ? Math.round((ratedCount / completedCount) * 100) : 0;

  const handleRatingSubmit = async (
    attendanceId: string | null,
    rating: number,
    notes: string,
    bookingId: string
  ) => {
    // `updateHistoryRating` lève en cas d'échec : on ne referme le formulaire
    // qu'après un enregistrement réellement abouti.
    await updateHistoryRating(attendanceId, rating, notes, bookingId);
    setEditingId(null);
    setRatingModal({ open: false });
  };

  // Date de séance potentiellement invalide (champ vide côté serveur) :
  // `format` LÈVE en date-fns v3, ce qui blanchit toute la page.
  const safeDate = (value: Date | string) => {
    const d = value instanceof Date ? value : new Date(value);
    return isValid(d) ? format(d, "PPP", { locale: dateLocale }) : "—";
  };

  const renderStars = (rating?: number) =>
    Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={cn(
          "h-4 w-4 md:h-5 md:w-5 transition-all",
          rating && i < rating
            ? "text-yellow-400 fill-yellow-400 scale-110"
            : "text-muted-foreground"
        )}
      />
    ));

  return (
    <div className="space-y-4">
      {/* Toolbar sticky */}
      <div className={cn("sticky top-0 z-20 bg-card/60 backdrop-blur-xl border border-border/40 shadow-lg px-4 py-3 rounded-2xl", compact && "relative top-auto border-x-0 border-t-0 rounded-none bg-transparent shadow-none px-0")}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="font-extrabold text-xl md:text-2xl flex items-center gap-2">
              {!compact && <>🏆{" "}</>}
              {t(compact ? "history.sessionsHistory" : "history.myTrainingLog", {
                ns: "athlete",
                defaultValue: compact ? "Historique des séances" : "Mon carnet d'entraînement",
              })}
            </h2>
            <div className="text-xs text-muted-foreground mt-1">
              {t("history.motivateText", {
                defaultValue:
                  "Plus tu évalues tes séances, plus tu progresses ! 🎯",
              })}
            </div>
          </div>

          {!isVb20 && <div className="flex items-center gap-2">
            {/* Toggle vues */}
            <div className="inline-flex rounded-lg border overflow-hidden">
              <button
                className={cn(
                  "px-3 py-2 text-sm flex items-center gap-2",
                  viewMode === "cards"
                    ? "bg-primary text-primary-foreground"
                    : "bg-background"
                )}
                onClick={() => setViewMode("cards")}
                aria-pressed={viewMode === "cards"}
              >
                <LayoutGrid className="h-4 w-4" />
                {t("history.viewCards", {
                  ns: "athlete",
                  defaultValue: "Cartes",
                })}
              </button>
              <button
                className={cn(
                  "px-3 py-2 text-sm flex items-center gap-2 border-l",
                  viewMode === "table"
                    ? "bg-primary text-primary-foreground"
                    : "bg-background"
                )}
                onClick={() => setViewMode("table")}
                aria-pressed={viewMode === "table"}
              >
                <List className="h-4 w-4" />
                {t("history.viewTable", {
                  ns: "athlete",
                  defaultValue: "Tableau",
                })}
              </button>
            </div>

            {/* Page size */}
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                const n = Number(v);
                setPageSize(n);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[96px]">
                <SelectValue placeholder={t("athleteHistory.pageSizeLabel", { ns: "appui" })} />
              </SelectTrigger>
              <SelectContent align="end">
                {[6, 9, 12, 24, 48].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}/p
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>}
        </div>

        {/* KPIs + Recherche */}
        <div className={cn("mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3", isVb20 && "hidden")}>
          <div className="flex items-end gap-3">
            {toRate.length > 0 ? (
              <span className="px-3 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs font-bold animate-pulse backdrop-blur-sm">
                {t("history.sessionsToRate", { count: toRate.length })}
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full bg-green-500/15 text-green-700 dark:text-green-400 text-xs font-bold backdrop-blur-sm">
                {t("history.allRated", {
                  defaultValue: "Toutes vos séances sont notées, bravo !",
                })}
              </span>
            )}
            <div className="hidden md:block">
              <div className="w-40 bg-muted/30 h-2 rounded">
                <div
                  className="bg-yellow-400 h-2 rounded"
                  style={{
                    width: `${percentRated}%`,
                    transition: "width 0.3s",
                  }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {ratedCount}/{completedCount} ({percentRated}%)
              </span>
            </div>
          </div>

          <Input
            placeholder={t("history.searchPlaceholder", {
              ns: "athlete",
              defaultValue: "Rechercher par titre ou coach",
            })}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="w-full md:w-80"
          />
        </div>
      </div>

      {/* Contenu */}
      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">
          {t("athleteHistory.loading", { ns: "appui" })}
        </div>
      ) : error && history.length === 0 ? (
        // Un échec de chargement ne doit pas s'afficher « Aucun historique » :
        // l'adhérent croyait n'avoir jamais participé à une séance.
        <div className="text-center py-16 flex flex-col items-center gap-3">
          <div className="text-destructive font-semibold">
            {t("history.loadError", {
              ns: "athlete",
              defaultValue: "Impossible de charger ton historique",
            })}
          </div>
          <Button variant="outline" onClick={() => fetchHistory()}>
            {t("common.retry", { defaultValue: "Réessayer" })}
          </Button>
        </div>
      ) : total === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          {t("history.noHistory", {
            ns: "athlete",
            defaultValue: "Aucun historique",
          })}
        </div>
      ) : viewMode === "table" && !isVb20 ? (
        // Vue TABLEAU — pas de saisie inline; bouton "Noter" -> modale
        <div className="overflow-x-auto rounded-xl border border-border/40 bg-card/60 backdrop-blur-xl shadow-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/20 backdrop-blur-sm">
              <tr>
                <th className="text-left p-3">{t("athleteHistory.dateColumn", { ns: "appui" })}</th>
                <th className="text-left p-3">
                  {t("time", { ns: "classes", defaultValue: "Heure" })}
                </th>
                <th className="text-left p-3">
                  {t("title", { ns: "classes", defaultValue: "Cours" })}
                </th>
                <th className="text-left p-3">
                  {t("coach", { ns: "classes", defaultValue: "Coach" })}
                </th>
                <th className="text-left p-3">
                  {t("rating", { ns: "athlete", defaultValue: "Note" })}
                </th>
                <th className="text-left p-3">
                  {t("actions", { defaultValue: "Actions" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {safeDate(item.date)}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {item.time} ({item.duration}{" "}
                      {t("minutes", { ns: "classes" })})
                    </div>
                  </td>
                  <td className="p-3">{item.title}</td>
                  <td className="p-3">
                    {item.assignedCoach ? (
                      <div className="flex flex-col">
                        <span className="font-medium">{item.assignedCoach}</span>
                        {item.assignedCoach !== item.coach && (
                          <span className="text-xs text-muted-foreground">
                            ({item.coach})
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{item.coach}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex">{renderStars(item.rating)}</div>
                  </td>
                  <td className="p-3">
                    <Dialog
                      open={ratingModal.open && ratingModal.itemId === item.id}
                      onOpenChange={(open) =>
                        setRatingModal(
                          open
                            ? {
                                open,
                                itemId: item.id,
                                attendanceId: item.attendance_id,
                              }
                            : { open: false }
                        )
                      }
                    >
                      <DialogTrigger asChild>
                        <Button size="sm" variant="secondary">
                          {t("history.rate", {
                            ns: "athlete",
                            defaultValue: "Noter",
                          })}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>
                            {t("history.rateSession", {
                              ns: "athlete",
                              defaultValue: "Noter la séance",
                            })}
                          </DialogTitle>
                        </DialogHeader>
                        <SessionRating
                          attendanceId={item.attendance_id}
                          initialRating={item.rating || 0}
                          initialNote={item.notes || ""}
                          bookingId={item.id}
                          onSubmit={(rating, notes) =>
                            handleRatingSubmit(
                              item.attendance_id ?? null,
                              rating,
                              notes,
                              item.id
                            )
                          }
                        />
                        <DialogFooter>
                          <Button
                            onClick={() => setRatingModal({ open: false })}
                            variant="outline"
                          >
                            {t("close", { defaultValue: "Fermer" })}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Vue CARTES — saisie inline (comme avant)
         <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {currentItems.map((item, index) => (
            <div
              key={item.id}
              className={cn(
                 "athlete-history-card rounded-2xl p-4 shadow-lg transition-all relative flex flex-col min-h-[168px]",
                 "bg-card/60 backdrop-blur-sm border border-border/40 hover:shadow-xl hover:scale-[1.02]"
              )}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  {item.title}
                  {item.rating === 5 && (
                    <span
                      title={t("athleteHistory.topSession", { ns: "appui" })}
                      className="ml-2 text-yellow-400 text-xl"
                    >
                      ⭐
                    </span>
                  )}
                </h3>
                 <span className={cn("athlete-history-status px-2 py-1 rounded-full text-xs font-medium", item.status === "completed" ? "is-attended" : "is-missed")}>
                   {item.status === "completed"
                     ? t("history.attended", { ns: "athlete" })
                     : t(item.status === "cancelled" ? "history.cancelled" : "history.missed", { ns: "athlete" })}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 text-sm text-muted-foreground mb-2">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {safeDate(item.date)}
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {item.time} ({item.duration} {t("minutes", { ns: "classes" })})
                </div>
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {item.assignedCoach ? (
                    <span className="font-medium text-green-700 dark:text-green-400">
                      {item.assignedCoach}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{item.coach}</span>
                  )}
                </div>
              </div>

              <div className="mt-auto">
                {/* Clé d'édition = identifiant de RÉSERVATION. `attendance_id`
                    peut être nul (la RPC ne le renvoie pas) et `null !== null`
                    étant faux, toute séance déjà notée réaffichait le
                    formulaire, « Modifier mon avis » restant inatteignable. */}
                {item.rating && editingId !== item.id ? (
                  <div className="flex items-center gap-2">
                    {renderStars(item.rating)}
                    {item.notes && (
                      <span className="ml-2 italic text-xs text-muted-foreground">
                        {item.notes}
                      </span>
                    )}
                     <Button
                       type="button"
                       variant="outline"
                       size="sm"
                       className="athlete-history-review ml-auto text-xs"
                      onClick={() => setEditingId(item.id)}
                    >
                      {t("history.editRating", {
                        ns: "athlete",
                        defaultValue: "Modifier mon avis",
                      })}
                     </Button>
                  </div>
                ) : (
                  <div className="bg-amber-500/10 border border-amber-500/20 backdrop-blur-sm p-2 rounded-lg mt-2">
                    <SessionRating
                      attendanceId={item.attendance_id}
                      initialRating={item.rating || 0}
                      initialNote={item.notes || ""}
                      bookingId={item.id}
                      onSubmit={(rating, notes) =>
                        handleRatingSubmit(
                          item.attendance_id ?? null,
                          rating,
                          notes,
                          item.id
                        )
                      }
                    />
                    <div className="text-xs mt-1 text-amber-600 dark:text-amber-400">
                      {t("history.ratingReminder", {
                        defaultValue:
                          "Aide-nous à améliorer les séances : laisse ton avis !",
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination footer */}
      {!isLoading && total > pageSize && (
        <div className="flex items-center justify-between gap-3 pt-2 bg-card/40 backdrop-blur-sm rounded-xl px-4 py-3 border border-border/30">
          <div className="text-sm text-muted-foreground">
            {t("history.paginationInfo", {
              ns: "athlete",
              defaultValue: "{{from}}–{{to}} sur {{total}}",
              from: total === 0 ? 0 : startIndex + 1,
              to: Math.min(startIndex + pageSize, total),
              total,
            })}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
            >
              {t("previous", { defaultValue: "Précédent" })}
            </Button>
            <span className="text-sm">
              {safePage} / {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={safePage === pageCount}
            >
              {t("next", { defaultValue: "Suivant" })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}